import type { Texture } from 'pixi.js';
import { Assets, Container, Graphics, Sprite, Text } from 'pixi.js';
import type { GameState, GameStateManager } from './GameStateManager';
import { BaseState } from './BaseState';
import { COL_LEFT_X, LEFT_W } from '../rendering/LayoutConstants';

const ACCENT = 0xDD2233;

// Transition duration in seconds
const TRANSITION_DURATION = 0.8;

export class TitleState implements GameState {
  private container = new Container();
  private manager: GameStateManager;

  // Logo elements
  private logoBase: Sprite | null = null;
  private logoDetail: Sprite | null = null;
  private logoBaseScale = 1;
  private logoTime = 0;

  // "Click to start" text
  private startText: Text | null = null;

  // Background animation state (same as BaseState)
  private circuitGfx: Graphics | null = null;
  private circuitSegments: { x1: number; y1: number; x2: number; y2: number; tier: number }[] = [];
  private scanLine: Graphics | null = null;
  private _scanLineH = 0; // cached screen height for scanline rebuild
  private pulseGfx: Graphics | null = null;
  private bgTime = 0;
  private circuitBaseAlpha = 1;
  private pulses: { segIdx: number; t: number; speed: number; tier: number }[] = [];

  // Transition state
  private transitioning = false;
  private transitionTime = 0;
  private logoStartX = 0;
  private logoStartY = 0;
  private logoStartScale = 1;
  private logoTargetX = 0;
  private logoTargetY = 0;
  private logoTargetScale = 1;

  // Detail sprite transition anchors
  private detailStartX = 0;
  private detailStartY = 0;
  private detailTargetX = 0;
  private detailTargetY = 0;


  constructor(manager: GameStateManager) {
    this.manager = manager;
  }

  enter() {
    const app = this.manager.app;
    app.stage.addChild(this.container);

    const W = app.screen.width;
    const H = app.screen.height;

    // Dark background
    const bg = new Graphics();
    bg.rect(0, 0, W, H);
    bg.fill(0x080a10);
    this.container.addChild(bg);

    // Grid overlay (same as BaseState)
    const grid = new Graphics();
    for (let x = 0; x < W; x += 40) { grid.moveTo(x, 0); grid.lineTo(x, H); }
    for (let y = 0; y < H; y += 40) { grid.moveTo(0, y); grid.lineTo(W, y); }
    grid.stroke({ color: 0x111822, width: 1 });
    for (let x = 0; x < W; x += 80) { grid.moveTo(x, 0); grid.lineTo(x, H); }
    for (let y = 0; y < H; y += 80) { grid.moveTo(0, y); grid.lineTo(W, y); }
    grid.stroke({ color: 0x16202e, width: 1 });
    this.container.addChild(grid);

    // Top accent line
    const topLine = new Graphics();
    topLine.moveTo(0, 0); topLine.lineTo(W, 0);
    topLine.stroke({ color: ACCENT, width: 2 });
    this.container.addChild(topLine);

    this.buildCircuitBackground(W, H);

    // Load logo — displayed large and centered
    void Promise.all([
      Assets.load<Texture>('/logo-base.svg'),
      Assets.load<Texture>('/logo-detail.svg'),
    ]).then(([baseTex, detailTex]) => {
      // Large centered logo — roughly 60% of screen width, capped at 700px
      const logoWidth = Math.min(W * 0.6, 700);
      const scale = logoWidth / baseTex.width;
      const logoHeight = baseTex.height * scale;
      const logoX = (W - logoWidth) / 2;
      // Center the logo+text group vertically (logo + 40px gap + ~30px text)
      const groupHeight = logoHeight + 40 + 30;
      const logoY = (H - groupHeight) / 2;

      const svgW = 988, svgH = 395;
      const texScaleX = baseTex.width / svgW;
      const texScaleY = baseTex.height / svgH;

      const logoBase = new Sprite(baseTex);
      logoBase.width = logoWidth;
      logoBase.height = logoHeight;
      logoBase.position.set(logoX, logoY);
      this.container.addChild(logoBase);
      this.logoBase = logoBase;

      const detail = new Sprite(detailTex);
      detail.scale.set(scale);
      const pivotX = 932 * texScaleX;
      const pivotY = 95 * texScaleY;
      detail.pivot.set(pivotX, pivotY);
      detail.position.set(logoX + pivotX * scale, logoY + pivotY * scale);
      this.container.addChild(detail);
      this.logoDetail = detail;
      this.logoBaseScale = scale;

      // Store start positions for transition
      this.logoStartX = logoX;
      this.logoStartY = logoY;
      this.logoStartScale = scale;

      // Compute target positions (BaseState corner position)
      const targetScale = LEFT_W / baseTex.width;
      this.logoTargetX = COL_LEFT_X;
      this.logoTargetY = 8;
      this.logoTargetScale = targetScale;

      // Detail sprite positions
      this.detailStartX = logoX + pivotX * scale;
      this.detailStartY = logoY + pivotY * scale;
      this.detailTargetX = COL_LEFT_X + pivotX * targetScale;
      this.detailTargetY = 8 + pivotY * targetScale;

      // "Click to start" text — below logo
      const text = new Text({
        text: 'CLICK TO START',
        style: {
          fontFamily: 'Gugi',
          fontSize: 24,
          fill: 0x8899aa,
          letterSpacing: 4,
        },
      });
      text.anchor.set(0.5, 0);
      text.position.set(W / 2, logoY + logoHeight + 40);
      this.container.addChild(text);
      this.startText = text;
    });

    // Click handler
    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';
    this.container.on('pointerdown', () => this.startTransition());
  }

  private startTransition() {
    if (this.transitioning || !this.logoBase) return;
    this.transitioning = true;
    this.transitionTime = 0;
    this.container.cursor = 'default';
    this.container.removeAllListeners('pointerdown');
  }

  /** Smooth ease-in-out curve */
  private easeInOutCubic(t: number): number {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  update(dt: number) {
    this.logoTime += dt;
    this.bgTime += dt;

    // Animate logo detail (rotation + pulse)
    if (this.logoDetail && !this.transitioning) {
      this.logoDetail.rotation = this.logoTime * 0.5;
      const pulse = this.logoBaseScale * (1 + Math.sin(this.logoTime * 1.2) * 0.08);
      this.logoDetail.scale.set(pulse);
    }

    // Flashing "click to start" text
    if (this.startText && !this.transitioning) {
      const flash = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(this.logoTime * 2.5));
      this.startText.alpha = flash;
    }

    // Background animations
    this.updateBackground(dt);

    // Transition animation
    if (this.transitioning) {
      this.transitionTime += dt;
      const rawT = Math.min(this.transitionTime / TRANSITION_DURATION, 1);
      const t = this.easeInOutCubic(rawT);

      // Fade out "click to start"
      if (this.startText) {
        this.startText.alpha = Math.max(0, 1 - rawT * 3); // fade out quickly
      }

      // Logo fades to 20% while it shrinks into the corner, matching the
      // subdued background-logo look in BaseState.
      const logoAlpha = 1 - t * 0.8;

      // Interpolate logo position and scale
      if (this.logoBase) {
        const currentScale = this.logoStartScale + (this.logoTargetScale - this.logoStartScale) * t;

        this.logoBase.position.set(
          this.logoStartX + (this.logoTargetX - this.logoStartX) * t,
          this.logoStartY + (this.logoTargetY - this.logoStartY) * t,
        );
        this.logoBase.width = this.logoBase.texture.width * currentScale;
        this.logoBase.height = this.logoBase.texture.height * currentScale;
        this.logoBase.alpha = logoAlpha;
      }

      // Interpolate detail sprite
      if (this.logoDetail) {
        const currentScale = this.logoStartScale + (this.logoTargetScale - this.logoStartScale) * t;
        this.logoDetail.scale.set(currentScale);
        this.logoDetail.position.set(
          this.detailStartX + (this.detailTargetX - this.detailStartX) * t,
          this.detailStartY + (this.detailTargetY - this.detailStartY) * t,
        );
        // Continue rotation during transition
        this.logoDetail.rotation = this.logoTime * 0.5;
        this.logoDetail.alpha = logoAlpha;
      }

      // Transition complete — switch to BaseState
      if (rawT >= 1) {
        this.manager.switchTo(new BaseState(this.manager, true));
      }
    }
  }

  private updateBackground(dt: number) {
    // Gentle circuit alpha breathing
    if (this.circuitGfx) {
      this.circuitGfx.alpha = this.circuitBaseAlpha * (1 + Math.sin(this.bgTime * 0.3) * 0.08);
    }

    // Scan line sweep — pre-rendered band, translate position + alpha per frame
    if (this.scanLine) {
      const W = this.manager.app.screen.width;
      const H = this.manager.app.screen.height;

      // Rebuild the static band only when screen height changes
      if (H !== this._scanLineH) {
        this._scanLineH = H;
        this.scanLine.clear();
        for (let i = -30; i <= 30; i++) {
          const dist = Math.abs(i) / 30;
          const a = (1 - dist * dist) * 0.035;
          this.scanLine.moveTo(i, 0);
          this.scanLine.lineTo(i, H);
          this.scanLine.stroke({ color: ACCENT, width: 1, alpha: a });
        }
      }

      const period = 12;
      const phase = (this.bgTime % period) / period;
      const scanX = phase * (W + 200) - 100;
      const edgeFade = Math.min(phase * 5, (1 - phase) * 5, 1);
      this.scanLine.position.x = scanX;
      this.scanLine.alpha = edgeFade;
    }

    // Energy pulses
    if (this.pulseGfx && this.circuitSegments.length > 0) {
      this.pulseGfx.clear();
      const TIERS_COLORS = [0xDD2233, 0xBB1122, 0x881122];
      const TIERS_ALPHA = [0.7, 0.5, 0.35];

      for (const p of this.pulses) {
        p.t += p.speed * dt;
        if (p.t > 1) {
          p.t -= 1;
          p.segIdx = (p.segIdx + 1 + Math.floor(Math.random() * 3)) % this.circuitSegments.length;
          p.tier = this.circuitSegments[p.segIdx]!.tier;
        }
        const seg = this.circuitSegments[p.segIdx]!;
        const x = seg.x1 + (seg.x2 - seg.x1) * p.t;
        const y = seg.y1 + (seg.y2 - seg.y1) * p.t;
        const col = TIERS_COLORS[p.tier]!;
        const a = TIERS_ALPHA[p.tier]!;

        this.pulseGfx.circle(x, y, 6);
        this.pulseGfx.fill({ color: col, alpha: a * 0.25 });
        this.pulseGfx.circle(x, y, 2);
        this.pulseGfx.fill({ color: col, alpha: a });
      }
    }
  }

  private buildCircuitBackground(W: number, H: number) {
    let seed = 0xDEADBEEF;
    const rand = () => {
      seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5;
      return (seed >>> 0) / 0xFFFFFFFF;
    };

    const GRID = 80;
    const cols = Math.ceil(W / GRID) + 1;
    const rows = Math.ceil(H / GRID) + 1;

    const TIERS: [number, number, number, number, number][] = [
      [2.5, 12, 0xDD2233, 0.38, 0.07],
      [1.5,  8, 0xBB1122, 0.26, 0.05],
      [1.0,  5, 0x881122, 0.16, 0.03],
    ];

    type Seg = { x1: number; y1: number; x2: number; y2: number; tier: number };
    const segments: Seg[] = [];
    const viaPads = new Map<string, number>();

    const addSeg = (x1: number, y1: number, x2: number, y2: number, tier: number) => {
      segments.push({ x1, y1, x2, y2, tier });
      const key1 = `${x1},${y1}`, key2 = `${x2},${y2}`;
      const prev1 = viaPads.get(key1) ?? 999;
      const prev2 = viaPads.get(key2) ?? 999;
      if (tier < prev1) viaPads.set(key1, tier);
      if (tier < prev2) viaPads.set(key2, tier);
    };

    const traceCount = Math.floor(cols * rows * 0.4);
    for (let t = 0; t < traceCount; t++) {
      const tier = rand() < 0.15 ? 0 : rand() < 0.35 ? 1 : 2;
      let cx = Math.floor(rand() * cols) * GRID;
      let cy = Math.floor(rand() * rows) * GRID;
      const len = 2 + Math.floor(rand() * 5);
      let dir = Math.floor(rand() * 4);
      const dx = [1, 0, -1, 0] as const;
      const dy = [0, 1, 0, -1] as const;

      for (let step = 0; step < len; step++) {
        const nx = cx + dx[dir]! * GRID;
        const ny = cy + dy[dir]! * GRID;
        if (nx < -GRID || ny < -GRID || nx > W + GRID || ny > H + GRID) break;
        addSeg(cx, cy, nx, ny, tier);
        cx = nx; cy = ny;
        if (rand() < 0.4) {
          dir = rand() < 0.5 ? (dir + 1) % 4 : (dir + 3) % 4;
        }
      }
    }

    const g = new Graphics();
    g.alpha = 1;

    for (const { x1, y1, x2, y2, tier } of segments) {
      const [, gw, col, , ga] = TIERS[tier]!;
      g.moveTo(x1, y1); g.lineTo(x2, y2);
      g.stroke({ color: col, width: gw, alpha: ga });
    }
    for (const { x1, y1, x2, y2, tier } of segments) {
      const [lw, , col, ca] = TIERS[tier]!;
      g.moveTo(x1, y1); g.lineTo(x2, y2);
      g.stroke({ color: col, width: lw, alpha: ca });
    }

    for (const [key, tier] of viaPads) {
      const [px, py] = key.split(',').map(Number) as [number, number];
      const [lw, gw, col, ca, ga] = TIERS[tier]!;
      const r = lw + 1.5;
      g.circle(px, py, r + gw / 2);
      g.fill({ color: col, alpha: ga * 1.5 });
      g.circle(px, py, r + 1);
      g.stroke({ color: col, width: 0.8, alpha: ca * 0.6 });
      g.circle(px, py, r * 0.55);
      g.fill({ color: col, alpha: ca });
    }

    this.container.addChild(g);
    this.circuitGfx = g;
    this.circuitSegments = segments;
    this.circuitBaseAlpha = g.alpha;

    const scan = new Graphics();
    scan.alpha = 0;
    this.container.addChild(scan);
    this.scanLine = scan;

    const pulse = new Graphics();
    this.container.addChild(pulse);
    this.pulseGfx = pulse;

    const PULSE_COUNT = 12;
    for (let i = 0; i < PULSE_COUNT; i++) {
      const segIdx = Math.floor(rand() * segments.length);
      this.pulses.push({
        segIdx,
        t: rand(),
        speed: 0.15 + rand() * 0.25,
        tier: segments[segIdx]!.tier,
      });
    }
  }

  exit() {
    this.logoBase = null;
    this.logoDetail = null;
    this.startText = null;
    this.circuitGfx = null;
    this.scanLine = null;
    this.pulseGfx = null;
    this.circuitSegments = [];
    this.pulses = [];
    this.manager.app.stage.removeChild(this.container);
    this.container.destroy({ children: true });
  }
}
